/**
 * How a live map ENDS without a usable map — the outcome half of
 * `MapBoundary`'s `LiveMap`, which mounts these and hands every event to the
 * boundary's phase machine (`./map-phase`).
 *
 * Two families, each reported ONCE and then dispatched:
 *
 *   * the script never arrived (#7696, #8204) — `onError`, or the deadline;
 *   * the script arrived and the map is still unusable (#9393) — Google
 *     refused the key (vis.gl's own `AUTH_FAILURE` status), or a
 *     marker-bearing map reported Advanced Markers unavailable (the SDK then
 *     opens its own modal "Error" dialog). Either unmounts the map: see
 *     `MapBoundary.tsx`'s header.
 *
 * Split out of `MapBoundary.tsx` so the boundary stays readable as the one
 * component that mounts a map; nothing here mounts `Map` or `APIProvider`.
 */
import { reportMapsFailure } from '@/shared/lib/maps-health';
import { MAPS_SCRIPT_LOAD_DEADLINE_MS } from '@letsgethome/lib/telemetry/transport';
import { useCallback, useEffect, useRef } from 'react';

import {
  UNAVAILABLE_CAUSE_OF,
  type MapUnavailableEvent,
  type MapUnusableEvent,
  type ScriptLoadEndedEvent,
} from './map-phase';
import { useAdvancedMarkersUnavailable, useMapsKeyRefused } from './vis-gl';

/**
 * The SDK load's outcome, decided ONCE: the first of `onLoad`, `onError` and
 * the deadline wins and the others are ignored — so a hung request that errors
 * after the deadline is not a second report, and a load that lands after it
 * does not resurrect a map the reader has already been given a fallback for.
 * Armed after `<APIProvider>`'s own mount effect (a child's effects run first),
 * i.e. once the script tag is in the document. `onEnded` must be stable (it is
 * the boundary's `dispatch`): the provider captures its callbacks on mount.
 */
export function useScriptLoadOutcome(onEnded: (event: MapUnavailableEvent) => void) {
  const settled = useRef(false);
  const deadline = useRef<ReturnType<typeof setTimeout>>(undefined);
  const settle = useCallback(
    (event?: ScriptLoadEndedEvent, context?: Record<string, unknown>) => {
      if (settled.current) return;
      settled.current = true;
      clearTimeout(deadline.current);
      if (!event) return;
      reportMapsFailure(UNAVAILABLE_CAUSE_OF[event.kind], context);
      onEnded(event);
    },
    [onEnded]
  );
  useEffect(() => {
    deadline.current = setTimeout(
      () => settle({ kind: 'script_load_timed_out' }),
      MAPS_SCRIPT_LOAD_DEADLINE_MS
    );
    return () => clearTimeout(deadline.current);
  }, [settle]);
  return settle;
}

/**
 * Report a loaded-but-unusable map ONCE and hand the event to the boundary
 * (#9393). Disarms the script deadline first: a refusal can land before
 * `onLoad`, and the fallback it opens must not be followed by a timeout report.
 */
export function useUnusableOutcome(
  settle: () => void,
  onUnavailable: (event: MapUnavailableEvent) => void
) {
  const ended = useRef(false);
  return useCallback(
    (event: MapUnusableEvent) => {
      if (ended.current) return;
      ended.current = true;
      settle();
      reportMapsFailure(UNAVAILABLE_CAUSE_OF[event.kind]);
      onUnavailable(event);
    },
    [settle, onUnavailable]
  );
}

interface WatchProps {
  onUnusable: (event: MapUnusableEvent) => void;
}

/** Inside `APIProvider`: Google refused the key (vis.gl's own status). */
export function KeyRefusalWatch({ onUnusable }: WatchProps) {
  const refused = useMapsKeyRefused();
  useEffect(() => {
    if (refused) onUnusable({ kind: 'auth_failed' });
  }, [refused, onUnusable]);
  return null;
}

/** Inside `Map`: the map reported Advanced Markers unavailable. */
export function MarkerCapabilityWatch({ onUnusable }: WatchProps) {
  const unavailable = useAdvancedMarkersUnavailable();
  useEffect(() => {
    if (unavailable) onUnusable({ kind: 'advanced_markers_unavailable' });
  }, [unavailable, onUnusable]);
  return null;
}
